import type { LiveSession } from '@/types';
import { formatMatchClock } from '@/types';
import { LIVE_PERIOD_LABELS, LIVE_STATUS_LABELS } from '@/utils/liveClock';

export function buildLiveSessionTitle(parts: {
  competition_name?: string | null;
  home_team?: string | null;
  away_team?: string | null;
}): string {
  const home = parts.home_team?.trim();
  const away = parts.away_team?.trim();
  const comp = parts.competition_name?.trim();
  if (home && away) return comp ? `${comp} · ${home} vs ${away}` : `${home} vs ${away}`;
  if (comp) return comp;
  if (home) return home;
  if (away) return away;
  return '即時記錄';
}

export function formatLiveMatchDate(date?: string | null): string | null {
  if (!date) return null;
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('zh-HK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  });
}

export function liveMatchHeadline(session: Pick<LiveSession, 'home_team' | 'away_team'>): string {
  const home = session.home_team?.trim() || '主隊';
  const away = session.away_team?.trim() || '客隊';
  return `${home} vs ${away}`;
}

export function liveSessionMetaLine(session: LiveSession): string {
  const parts: string[] = [];
  if (session.competition_name?.trim()) parts.push(session.competition_name.trim());
  const date = formatLiveMatchDate(session.match_date);
  if (date) parts.push(date);
  if (session.match_duration_minutes) parts.push(`${session.match_duration_minutes} 分鐘`);
  return parts.join(' · ');
}

export function liveClockSummary(session: LiveSession): string {
  const period =
    LIVE_PERIOD_LABELS[session.current_period as keyof typeof LIVE_PERIOD_LABELS] ||
    session.current_period;
  const status = LIVE_STATUS_LABELS[session.status] || session.status;
  const clock =
    session.current_match_second != null
      ? formatMatchClock(session.current_match_second)
      : formatMatchClock(session.accumulated_seconds || 0);
  return `${clock} · ${period} · ${status}`;
}

export function liveStatusColor(status: string): 'secondary' | 'success' | 'warning' | 'default' {
  if (status === 'live') return 'success';
  if (status === 'paused' || status === 'halftime') return 'warning';
  if (status === 'ended') return 'default';
  return 'secondary';
}
